import { useEffect, useState } from 'react';
import { Toolbar, Drawer, Box, Slide, Stack, Link, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DrawerContent from './DrawerContent';
import SubMenuContent from './SubMenuContent';
import { Icons, HXCIcon } from '../../HXCIcon';
import { IconButton } from '../../IconButton';
import type { LocalLinkData, MenuLinkData } from '../LocalLinks/LocalLinks';
import { SearchBar } from '../SearchBar/SearchBar';
import { useHandleSearch } from '../SearchBar/useHandleSearch';
import type { MobileNavProps } from './MobileNav.types';
import { MOBILE_NAV_TOOLBAR_GUTTER_SPACING } from '../styles';

export const MobileNav = (props: MobileNavProps) => {
  const { linkComponent, showMyOrderLink, myOrderHref, localNavProps } = props;

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [currentMenu, setCurrentMenu] = useState<'main' | MenuLinkData>('main');

  // Type guard to check if an item is MenuLinkData
  function isMenuLinkData(link: LocalLinkData | MenuLinkData): link is MenuLinkData {
    return (link as MenuLinkData).options !== undefined;
  }

  const menuLinks: MenuLinkData[] = props.localLinksData?.filter(isMenuLinkData) || [];

  const handleMenuClick = (menuLink: MenuLinkData) => {
    setCurrentMenu(menuLink);
  };

  const handleBackClick = () => {
    setCurrentMenu('main');
  };

  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const isTiny = useMediaQuery('(max-width:360px)');

  const { handleSearch, searchTerm, anchorEl, setAnchorEl } = useHandleSearch(
    props.onSearch,
    props.localNavProps?.initialSearchTerm,
  );

  useEffect(() => {
    setDrawerOpen(false);
  }, [localNavProps.currentUrl, localNavProps.authState]);

  return (
    <Box data-component="MobileNav">
      <Toolbar
        sx={{
          backgroundColor: theme.palette.black.main,
          justifyContent: 'space-between',
          gap: { xs: 1, sm: 1.5 },
          px: MOBILE_NAV_TOOLBAR_GUTTER_SPACING,
          height: '4.125rem',
          alignItems: 'center',
          display: 'flex',
          flexWrap: 'nowrap',
          boxSizing: 'border-box',
        }}
      >
        {(!searchOpen || (searchOpen && !isXs)) && (
          <Link
            component={linkComponent}
            to={'/'}
            aria-label={'Go to homepage'}
            role={'link'}
            sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}
          >
            <HXCIcon
              name={Icons.HxBasicWhiteLogo}
              color={'white'}
              {...(isTiny && { sizePx: 188 })}
              sx={{
                position: 'relative',
                top: isTiny ? 0 : '0.25rem',
              }}
            />
          </Link>
        )}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            minWidth: 0,
            transform: searchOpen ? 'scaleX(1)' : 'scaleX(0)',
            transformOrigin: 'right',
            transition: theme.transitions.create('transform', {
              duration: 300,
              easing: theme.transitions.easing.easeInOut,
            }),
            overflow: 'hidden',
          }}
        >
          <SearchBar
            onSearch={props.onSearch}
            handleSearch={handleSearch}
            searchTerm={searchTerm}
            searchPlaceholder={props?.searchPlaceholder}
            anchorEl={anchorEl}
            setAnchorEl={setAnchorEl}
          />
        </Box>
        <Stack direction="row" spacing={2} sx={{ flexShrink: 0, alignItems: 'center' }}>
          {Boolean(props.onSearch) && searchOpen && (
            <IconButton
              label="search"
              onClick={() => setSearchOpen((prev) => !prev)}
              iconName={Icons.CloseSm}
              iconColor={'white'}
            />
          )}
          {Boolean(props.onSearch) && !searchOpen && (
            <IconButton
              label="search"
              onClick={() => setSearchOpen((prev) => !prev)}
              iconName={Icons.SearchRg}
              iconColor={'white'}
            />
          )}
          {!searchOpen && showMyOrderLink && (
            <Link
              component={linkComponent}
              to={myOrderHref}
              aria-label={'view order'}
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <HXCIcon name={Icons.BasketFilledRg} color={'white'} />
            </Link>
          )}
          {!searchOpen && (
            <IconButton
              label="menu"
              onClick={() => setDrawerOpen(true)}
              iconName={Icons.MenuRg}
              iconColor={'white'}
            />
          )}
        </Stack>
      </Toolbar>

      <Drawer
        sx={{
          display: { xs: 'flex', md: 'none' },
        }}
        anchor="right"
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setCurrentMenu('main');
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '300px',
            height: '100%',
            backgroundColor: theme.palette.black.main,
            overflowX: 'hidden',
          }}
        >
          <DrawerContent
            {...props}
            onMenuClick={handleMenuClick}
            toggleDrawer={() => setDrawerOpen(false)}
            localNavProps={props.localNavProps}
          />
          {menuLinks.map((menuLink) => (
            <Slide
              key={menuLink.text}
              direction="left"
              in={currentMenu === menuLink}
              mountOnEnter
              unmountOnExit
            >
              <Box
                sx={{
                  position: 'absolute', // Position it on top of the main drawer content
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundColor: theme.palette.black.main,
                }}
              >
                <SubMenuContent menuLink={menuLink} onBackClick={handleBackClick} />
              </Box>
            </Slide>
          ))}
        </Box>
      </Drawer>
    </Box>
  );
};
